import { useEffect, useRef, useState } from 'react';

import { ApiError, call, type Item } from '../api';
import { Back, Picture, Top, useSignedIn } from '../components/parts';
import { addToPhone, bumpList, readList } from '../phone-list';
import { Link } from '../router';
import { cropOf, idOf, makeDetector, warmUp, type Detector } from '../scan-reader';

// Scan (José's pick, 23 Oct 2026): the phone's camera reads the QR code on each price sticker, one after another, and
// each card shows below the buttons, to add to My list. With no live camera (a page not on https, or the camera
// refused), a photo of the sticker works too. Signed out, My list stays on this phone; signed in, it is the account's.

type Row = { item: Item; onList: boolean };

const READ_EVERY_MS = 60;

export function ScanPage() {
  const signedIn = useSignedIn();
  const box = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const opening = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const detector = useRef<Detector | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const ticks = useRef(0);
  const seen = useRef(new Map<string, string>());
  const last = useRef({ text: '', at: 0 });
  const [on, setOn] = useState(false);
  const [say, setSay] = useState(signedIn ? 'Cards you add are saved to your account.' : 'Cards you add stay on this phone.');
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    warmUp();
    const off = () => document.hidden && stop('Camera off.');
    document.addEventListener('visibilitychange', off);
    return () => {
      document.removeEventListener('visibilitychange', off);
      stop();
    };
    // Once, as the page opens and closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stop(words?: string) {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    clearTimeout(timer.current);
    if (video.current) video.current.srcObject = null;
    setOn(false);
    if (words) setSay(words);
  }

  // The middle of what the box shows, or the whole frame every third read, for a sticker off to one side.
  function frame(): CanvasImageSource {
    const v = video.current!;
    ticks.current += 1;
    if (ticks.current % 3 === 0) return v;
    const [sx, sy, side, out] = cropOf(v.videoWidth, v.videoHeight, box.current?.clientWidth ?? 4, box.current?.clientHeight ?? 3);
    const c = (canvas.current ??= document.createElement('canvas'));
    c.width = out;
    c.height = out;
    c.getContext('2d', { willReadFrequently: true })?.drawImage(v, sx, sy, side, side, 0, 0, out, out);
    return c;
  }

  function tick() {
    if (!stream.current) return;
    const v = video.current;
    if (v && v.readyState >= 2 && v.videoWidth && detector.current) {
      detector.current
        .detect(frame())
        .then((codes) => {
          if (codes.length) found(codes[0]!.rawValue);
        })
        .catch(() => {})
        .then(() => {
          if (stream.current) timer.current = setTimeout(tick, READ_EVERY_MS);
        });
    } else timer.current = setTimeout(tick, READ_EVERY_MS);
  }

  // No camera opens on a page the buyer has left (its video is gone) or cannot see.
  function away() {
    return !video.current || document.hidden;
  }

  // stop() turns off only the camera in stream.current, and the camera takes a while to open, so each wait below is
  // followed by a check (redteam #247 B1: a camera nothing could turn off).
  async function start() {
    // The button still says Start camera while the camera opens: another tap asks for no second camera.
    if (opening.current) return;
    if (stream.current) return stop('Camera off.');
    if (!navigator.mediaDevices?.getUserMedia) return setSay('This page cannot open the camera here. Take a photo of the sticker instead.');
    opening.current = true;
    setSay('Opening the camera...');
    let s: MediaStream | null = null;
    try {
      detector.current = await makeDetector();
      if (away()) return;
      s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      // The page hid or closed while the camera opened, so its stop() found no camera to turn off: this one stops here.
      if (away() || stream.current) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = s;
      video.current!.srcObject = s;
      await video.current!.play();
      // Stopped while it began to play.
      if (stream.current !== s) return;
      setOn(true);
      setSay("Point the camera at a sticker's QR code.");
      tick();
    } catch {
      // A stop while the camera began to play makes play() fail; that stop has said so already.
      if (s && stream.current !== s) return;
      stop();
      setSay('The camera did not open. Allow the camera, or take a photo of the sticker instead.');
    } finally {
      opening.current = false;
    }
  }

  async function photo(file: File | undefined) {
    if (!file) return;
    setSay('Reading the photo...');
    try {
      const d = await makeDetector();
      const codes = await d.detect(await createImageBitmap(file));
      if (codes.length) found(codes[0]!.rawValue);
      else setSay('No QR code in that photo. Get closer to the sticker and try again.');
    } catch {
      setSay('Could not read that photo. Try again.');
    }
  }

  async function found(text: string) {
    const id = idOf(text);
    const now = Date.now();
    if (!id) {
      if (text !== last.current.text || now - last.current.at > 3000) setSay('That QR is not a GrailTab sticker.');
      last.current = { text, at: now };
      return;
    }
    if (id === last.current.text && now - last.current.at < 3000) return;
    last.current = { text: id, at: now };
    const name = seen.current.get(id);
    if (name) return setSay(`Already scanned: ${name}.`);
    seen.current.set(id, 'loading');
    navigator.vibrate?.(40);
    setSay('Found a card. Loading it...');
    try {
      const item = await call<Item>(`/api/find/items/${id}`);
      const onList = item.account ? item.account.on_list : readList().some((x) => x.id === item.id);
      seen.current.set(id, item.name);
      setRows((r) => [{ item, onList }, ...r]);
      setSay(`${item.name}${onList ? ' is on your list.' : '. Add it, or scan the next sticker.'}`);
    } catch (e) {
      seen.current.delete(id);
      setSay(e instanceof ApiError && e.status === 404 ? 'This card is no longer for sale.' : 'No signal. Point at the sticker again.');
    }
  }

  async function add(item: Item) {
    const mark = () => setRows((r) => r.map((x) => (x.item.id === item.id ? { ...x, onList: true } : x)));
    if (!signedIn) {
      if (!addToPhone(item)) return setSay('This browser cannot keep a list. Open the page in Safari or Chrome.');
      mark();
      return setSay(`Added ${item.name}. Your list stays on this phone.`);
    }
    try {
      const a = await call<{ status: string; max: number }>('/api/find/list', { method: 'POST', body: { id: item.id } });
      if (a.status === 'added') {
        mark();
        bumpList();
        setSay(`Added ${item.name}. Your list is saved to your account.`);
      } else setSay(a.status === 'full' ? `Your list is full (${a.max} cards). Remove some first.` : 'This card is no longer for sale.');
    } catch (e) {
      setSay(e instanceof ApiError && e.status === 0 ? 'No signal. Try again in a moment.' : 'That did not work. Try again.');
    }
  }

  return (
    <>
      <Top />
      <main>
        <Back href="/list" label="My list" />
        <h1>Scan stickers</h1>
        <p className="lede">Point the camera at the QR code on a card's price sticker. Each card shows below. Add the ones you want to My list.</p>
        <div id="scan" className={on ? 'scanbox on' : 'scanbox'} ref={box}>
          <video ref={video} playsInline muted aria-label="The camera" />
          <span className="aim" aria-hidden="true" />
          <span className="off">The camera is off</span>
        </div>
        <div className="acts-col">
          <button className="btn" type="button" onClick={start}>
            {on ? 'Stop camera' : 'Start camera'}
          </button>
          <label className="clear wide-btn">
            Take a photo of the sticker
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => {
                void photo(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
        </div>
        <p className="note" role="status">
          {say}
        </p>
        <div className="sres">
          {rows.length === 0 ? <p className="empty">No cards scanned yet.</p> : null}
          {rows.map(({ item, onList }) => (
            <div className="srow" key={item.id}>
              <Link className="thumb" href={`/i/${item.id}`} aria-label={`Open ${item.name}`}>
                <Picture src={item.picture} />
              </Link>
              <div className="sinfo">
                <Link className="nm" href={`/i/${item.id}`}>
                  {item.name}
                </Link>
                {[item.grade, item.set].filter(Boolean).join(' · ') ? <span className="gd">{[item.grade, item.set].filter(Boolean).join(' · ')}</span> : null}
                <span className={item.price ? 'pr' : 'pr ask'}>{item.price ?? 'Ask the seller'}</span>
                <span className="vd">{[item.shop, item.show].filter(Boolean).join(' · ')}</span>
                <button className="sadd" type="button" disabled={onList} onClick={() => add(item)}>
                  {onList ? 'On my list' : 'Add to my list'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </>
  );
}
